
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import moment from 'moment';
import {getTableName, getSqlColumnRef, sqlExtractDay} from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import settingx from '@/store/modules/settingx';
import { addComma } from '@/util/number';

@Component({
	components: {
		LineChartFilterable,
		IncludeTaxToggle,
		TableCollapseToggle,
	},
})
export default class DashboardCurrentMonthQuotationLineChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateAsOf'];

	public xAxisData: string[] = [];
	public periodTotalData: any[] = [];
	public periodTotalFields: any[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get currentMonth(): [number, number] {
		const startMonth = moment(this.selectedAsOfDate).startOf('month').valueOf();
		const endMonth = moment(this.selectedAsOfDate).endOf('day').valueOf();
		return [startMonth, endMonth];
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get expensiveHook() {
		const { includeTax, currentMonth } = this;
		return JSON.stringify([includeTax, currentMonth]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const amountCol = getSqlColumnRef('dashboardQuotation', this.includeTax ? 'amountWithTax' : 'amount');
		const transferCol = getSqlColumnRef('dashboardQuotation',
			this.includeTax ? 'transferredAmountWithTax' : 'transferredAmount');
		const table = getTableName('dashboardQuotation');

		const [monthStart, dayEnd] = this.currentMonth;
		const dayCount = moment(this.selectedAsOfDate).date();

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['dashboardQuotation']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const rows = await queryHead.rawQuery(`
			SELECT ${sqlExtractDay('"DOCDATE"', 'day_num')},
				SUM(TRY_CAST(${amountCol} AS DOUBLE)) as quotation_amt,
				SUM(TRY_CAST(${transferCol} AS DOUBLE)) as converted_amt
			FROM ${table}
			WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${monthStart} AND ${dayEnd}
			GROUP BY day_num
			ORDER BY day_num
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		const dayMap: Record<number, any> = {};
		for (const row of rows) {
			dayMap[Number(row.day_num)] = row;
		}

		const quotationData: number[] = [];
		const convertedData: number[] = [];
		const labels: string[] = [];
		let totalQuotation = 0;
		let totalConverted = 0;

		for (let d = 1; d <= dayCount; d++) {
			labels.push(String(d));
			const r = dayMap[d];
			const qAmt = r ? Number(r.quotation_amt) || 0 : 0;
			const cAmt = r ? Number(r.converted_amt) || 0 : 0;
			quotationData.push(qAmt);
			convertedData.push(cAmt);
			totalQuotation += qAmt;
			totalConverted += cAmt;
		}

		this.xAxisData = labels;

		this.periodTotalFields = [
			{ key: 'total_quotation', label: 'Total Quotation', textAlign: 'center' },
			{ key: 'total_converted_quotation', label: 'Total Converted Quotation', textAlign: 'center' },
		];

		this.periodTotalData = [
			{
				total_quotation: this.currencySymbol + ' ' + addComma(totalQuotation, this.numDecimal, true),
				total_converted_quotation: this.currencySymbol + ' '
					+ addComma(totalConverted, this.numDecimal, true),
			},
		];

		const quotation = {
			name: 'Quotation',
			data: quotationData,
			type: 'line',
			areaStyle: { color: GobiColor.COLOR1, opacity: 1 },
			smooth: true,
		};

		const convertedQuotation = {
			name: 'Converted Quotation',
			data: convertedData,
			type: 'line',
			areaStyle: { color: GobiColor.COLOR2, opacity: 1 },
			smooth: true,
		};

		this.series = [quotation, convertedQuotation];

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData', 'periodTotalData', 'periodTotalFields');
	}
}
